import { AppPlan, PlanLimit } from "models/interfaces";
import { SeatReductionRequired } from "types/perSeat";
import { getApiStatus, apiStatus } from "utils/apiStatus";
import { isUnlimitedLimit } from "helpers/methods";

/**
 * Shared per-seat billing logic — **buy-upfront** model.
 *
 * The customer buys a number of seats and is billed `seats × price`; the
 * subscription quantity is that purchased count, never the number of users.
 * Filling or freeing a seat by adding, deactivating or deleting a user costs
 * nothing and changes no billing. Money moves only at checkout, when the seat
 * count itself is changed ("manage seats"), on a plan change, and on cancel.
 *
 * Per-seat is opt-in: only plans whose `metadata` carries
 * `perSeatBilling = true` are sold this way. For every other plan the same
 * screens work but the price is flat at quantity 1, so the seat controls and
 * seat-cost UI must stay hidden.
 */

/** The plan-limit entity that seats are counted against. */
export const SEAT_ENTITY = "user";

const PER_SEAT_METADATA_KEY = "perSeatBilling";

/** A plan is sold by the seat only when its metadata says so (§7). */
export const isPerSeatPlan = (plan?: AppPlan | any): boolean =>
  (plan?.metadata ?? []).some(
    (entry: any) =>
      entry?.name === PER_SEAT_METADATA_KEY && String(entry?.value) === "true",
  );

/**
 * The plan's seat **cap** — the most seats this plan may ever be bought with,
 * read from a *catalog* plan's `user` limit. It is the product definition and
 * never changes.
 *
 * Two different numbers answer to the name `user.limit`, and mixing them is a
 * billing bug:
 * - **catalog** plan (`/secure/admin/{app}/plans`, or `/public/plan/account/…`
 *   which returns the catalog row for the account's plan id) → the **cap**;
 * - **account** plan (`/secure/billing/fetch/planLimit`) → the **seats bought**,
 *   overwritten on every billing event, and what actually gets billed.
 *
 * `active ≤ bought ≤ cap`. Use this for the seat picker's ceiling only — never
 * to bill or label. Returns `0` when the plan states no cap.
 */
export const getPlanSeatLimit = (plan?: AppPlan | any): number =>
  Number(
    (plan?.limits ?? []).find((limit: any) => limit?.entity === SEAT_ENTITY)
      ?.limit,
  ) || 0;

export interface SeatUsage {
  /** Seats filled by a billable user (Active or invited). */
  used: number;
  /** Seats the account has paid for — this is what gets billed. */
  limit: number;
  /** Paid-for seats nobody occupies yet. */
  available: number;
  /** 0–100, clamped. `0` when the plan has no seat limit. */
  percent: number;
  /** Every purchased seat is taken — adding another user will be refused. */
  isFull: boolean;
  /** 80% or more of the purchased seats consumed. */
  isNearLimit: boolean;
}

/**
 * Seat usage from `GET /secure/billing/fetch/planLimit`: `current` is the seats
 * in use, `limit` is the seats bought. The API omits either when it is `0`, so
 * missing means zero.
 */
export const getSeatUsage = (accountPlanLimit?: {
  appPlanLimitList?: PlanLimit[];
}): SeatUsage => {
  const entry = (accountPlanLimit?.appPlanLimitList ?? []).find(
    (limit: any) => limit?.entity === SEAT_ENTITY,
  );
  const used = Number(entry?.current) || 0;
  const limit = Number(entry?.limit) || 0;
  const percent = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  return {
    used,
    limit,
    available: Math.max(0, limit - used),
    percent,
    isFull: limit > 0 && used >= limit,
    isNearLimit: percent >= 80,
  };
};

/**
 * The `SeatReductionRequired` body behind a `409`, or undefined for any other
 * failure. A 409 body carries no `code`, so this relies on the HTTP status
 * `makeCall` attaches.
 */
export const getSeatReductionFromError = (
  error: any,
): SeatReductionRequired | undefined => {
  if (getApiStatus(error) !== apiStatus.seatReductionRequired) return undefined;
  return error as SeatReductionRequired;
};

/**
 * Why a seat action was refused: no free seat / the plan's seat cap (`406`), or
 * the card was declined while buying seats (`402`).
 */
export type SeatBlockReason = "limit" | "payment";

/**
 * Maps a rejected seat action onto the prompt to show. Anything else is a
 * genuine error and should surface as a normal message.
 */
export const getSeatBlockReason = (error: any): SeatBlockReason | undefined => {
  const status = getApiStatus(error);
  if (status === apiStatus.seatLimitReached) return "limit";
  if (status === apiStatus.paymentRequired) return "payment";
  return undefined;
};

/**
 * Whether a `406` was about the keep-list rather than about a full account.
 *
 * The status covers both — "no free seat" and "that selection can't be
 * honoured" (an id that is no longer billable, or more ids than seats) — and
 * only the message text tells them apart, which is the API's to reword. The
 * structural fact is reliable instead: a request that carried a selection was
 * not asking for a seat, so its refusal is about what was selected. That case
 * needs a fresh roster and another pass at the picker, not an upgrade prompt.
 */
export const isKeepSelectionRejected = (
  error: any,
  sentKeepUserIds?: number[],
): boolean =>
  Boolean(sentKeepUserIds?.length) &&
  getApiStatus(error) === apiStatus.seatLimitReached;

/** A seat check only needs the picker when it came back with users to choose. */
export const needsSeatReduction = (check?: SeatReductionRequired): boolean =>
  Boolean(check?.users && check.users.length > 0);

/** Amounts arrive in major units already (`9.0` = $9.00). */
export const formatMoney = (amount?: number, currency = "USD"): string => {
  const value = Number(amount) || 0;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
    }).format(value);
  } catch {
    // Unknown/blank currency code — fall back to a plain 2-decimal amount.
    return value.toFixed(2);
  }
};

/** Billing cadence is not returned directly; infer it from the validity (§3). */
export const getBillingInterval = (
  validityInDays?: number,
): "yearly" | "monthly" | "other" => {
  const days = Number(validityInDays) || 0;
  if (days === 365 || days === 366) return "yearly";
  if (days >= 28 && days <= 31) return "monthly";
  return "other";
};

/** "$9.00 / seat · billed yearly" strapline for a per-seat plan. */
export const formatPerSeatStrapline = (plan?: AppPlan | any): string => {
  const interval = getBillingInterval(plan?.validityInDays);
  const price = formatMoney(plan?.price, plan?.currency);
  return interval === "other"
    ? `${price} / seat`
    : `${price} / seat · billed ${interval}`;
};

/**
 * What the subscription bills: the **purchased** seat count times the plan
 * price. Never the number of users — the account pays for the seats it bought,
 * occupied or not. Non-per-seat plans bill flat, at quantity 1.
 */
export const getBilledSeatCount = (
  purchasedSeats: number,
  isPerSeat: boolean,
): number => (isPerSeat ? Math.max(1, purchasedSeats) : 1);

/**
 * The seat count to actually send with a purchase: a whole number, never below
 * one, and never above the plan's cap.
 *
 * A plan that states no `user` limit is **uncapped**, not single-seat. The
 * picker substitutes 1 for a missing cap so an unbounded plan can't look like
 * it covers any team size, but clamping a *purchase* with that stand-in would
 * quietly bill one seat for the whole account — so an absent or sentinel cap
 * imposes no ceiling here.
 */
export const clampSeatsToCap = (seats: number, cap: number): number => {
  const wanted = Math.max(1, Math.floor(Number(seats) || 0));
  const ceiling = Math.floor(Number(cap) || 0);
  return ceiling > 0 && !isUnlimitedLimit(ceiling)
    ? Math.min(wanted, ceiling)
    : wanted;
};
